import React from 'react';
import styled from 'styled-components';
import { formatDate } from '../utils/dateFormat';

const ResumeContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  font-family: ${props => props.styles?.fontFamily || 'Times New Roman, serif'};
  line-height: 1.6;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #000;
  
  h1 {
    font-size: 32px;
    margin: 0 0 0.5rem;
  }
  
  .contact-info {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    font-size: 14px;
    margin-bottom: 1rem;
  }

  .job-info {
    display: flex;
    justify-content: center;
    gap: 2rem;
    font-size: 14px;
    color: #666;
    margin-top: 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  h2 {
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 18px;
    border-bottom: 1px solid #000;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  .date-location {
    font-size: 14px;
    color: #666;
  }
  
  h3 {
    margin: 0 0 0.5rem;
  }
  
  .company {
    font-weight: bold;
  }
  
  .description {
    margin-top: 0.5rem;
    
    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    
    p {
      margin: 0.5rem 0;
    }
    
    li {
      margin: 0.25rem 0;
    }
    
    strong {
      font-weight: 600;
    }
    
    em {
      font-style: italic;
    }
  }
  
  .education-details {
    margin-top: 0.5rem;
    
    .gpa {
      color: #666;
      font-size: 0.9em;
      margin: 0.25rem 0;
    }
    
    .courses {
      color: #666;
      font-size: 0.9em;
      margin-top: 0.5rem;
      
      .course-list {
        margin-top: 0.25rem;
        font-style: italic;
      }
    }
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const ClassicTemplate = ({ data, styles }) => {
  const { personalInfo, education, experience, skills, certificates, technicalSkills, selfEvaluation } = data;

  return (
    <ResumeContainer styles={styles}>
      <Header>
        <h1>{personalInfo?.name}</h1>
        <div className="contact-info">
          <span>{personalInfo?.email}</span>
          <span>{personalInfo?.phone}</span>
          <span>{personalInfo?.address}</span>
        </div>
        <div className="job-info">
          {personalInfo?.workYears && (
            <span>工作年限: {personalInfo.workYears}年</span>
          )}
          {personalInfo?.jobIntention && (
            <span>求职意向: {personalInfo.jobIntention}</span>
          )}
          {personalInfo?.targetCity && (
            <span>意向城市: {personalInfo.targetCity}</span>
          )}
          {personalInfo?.expectedSalary && (
            <span>期望薪资: {personalInfo.expectedSalary}</span>
          )}
          {personalInfo?.availableDate && (
            <span>入职时间: {formatDate(personalInfo.availableDate)}</span>
          )}
        </div>
      </Header>

      {selfEvaluation && (
        <Section>
          <h2>自我评价</h2>
          <p>{selfEvaluation}</p>
        </Section>
      )}

      <Section>
        <h2>工作经验</h2>
        {experience?.map((exp, index) => (
          <ExperienceGrid key={index}>
            <div className="date-location">
              <div>{formatDate(exp.startDate)} - {formatDate(exp.endDate)}</div>
              <div>{exp.location}</div>
            </div>
            <div>
              <h3>{exp.position}</h3>
              <div className="company">{exp.company}</div>
              <div 
                className="description"
                dangerouslySetInnerHTML={{ 
                  __html: exp.description?.replace(/\n/g, '<br/>') || '' 
                }}
              />
            </div>
          </ExperienceGrid>
        ))}
      </Section>

      <Section>
        <h2>教育背景</h2>
        {education?.map((edu, index) => (
          <ExperienceGrid key={index}>
            <div className="date-location">
              <div>{formatDate(edu.startDate)} - {formatDate(edu.endDate)}</div>
            </div>
            <div>
              <h3>{edu.school}</h3>
              <div>{edu.degree}</div>
              <div>{edu.field}</div>
              <div className="education-details">
                {edu.showGpa && edu.gpa && (
                  <div className="gpa">专业成绩: {edu.gpa}</div>
                )}
                {edu.showCourses && edu.courses?.length > 0 && (
                  <div className="courses">
                    主修课程:
                    <div className="course-list">
                      {edu.courses.join(' · ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ExperienceGrid>
        ))}
      </Section>

      {technicalSkills?.length > 0 && (
        <Section>
          <h2>技术技能</h2>
          {technicalSkills.map((category, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <h3>{category.category}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                {category.items.map((item, i) => (
                  <span key={i}>
                    {item.name} ({item.level}/5)
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {certificates?.length > 0 && (
        <Section>
          <h2>荣誉证书</h2>
          {certificates.map((cert, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <h3>{cert.name}</h3>
              <p>发证机构: {cert.issuer}</p>
              <p>获得时间: {formatDate(cert.issueDate)}</p>
              {cert.description && <p>{cert.description}</p>}
            </div>
          ))}
        </Section>
      )}

      <Section>
        <h2>技能特长</h2>
        <div
          className="description"
          dangerouslySetInnerHTML={{
            __html: Array.isArray(skills) ? skills.join('<br/>') : (skills || '')
          }}
        />
      </Section>
    </ResumeContainer>
  );
};

export default ClassicTemplate; 